import type { UserTemplateData } from "@/shared/types";
import { normalizeUserData } from "@/shared/services/user-normalizer";
import { formatPhoneNumber } from "@/shared/services/phone-formatter";
import { getFromRoamingOrLocal, flushRoaming } from "@/shared/services/storage";
import { setupState } from "@/shared/services/setup-state";
import { profilePermissions } from "@/shared/services/profile-permissions";
import { info, warn, debug } from "@/shared/services/logger";

const SOURCE = "user-cache";
const LOCAL_KEY = "user_info";
const ROAMING_KEY = "helpitech_user_info";
const OVERRIDES_KEY = "user_overrides";
const LEGACY_META_KEY = "user_info_meta";
const IDENTITY_LOCAL_KEY = "identity_email";
const IDENTITY_ROAMING_KEY = "helpitech_identity_email";

/**
 * Campos da aba **Extras**: sempre editáveis pelo usuário, independente de
 * `allow_editing`, porque descrevem a pessoa e não o cadastro corporativo.
 *
 * `mobile_phone` entra aqui mesmo tendo origem no Graph: o atributo do Azure AD
 * quase nunca está preenchido, e sem um campo no formulário a assinatura saía
 * com o celular em branco sem o usuário ter como corrigir.
 */
export const EDITABLE_FIELDS = ["certifications", "ramal", "mobile_phone"] as const;

/**
 * Subconjunto de {@link EDITABLE_FIELDS} sem nenhuma fonte além do próprio
 * usuário  o valor no snapshot é a digitação anterior dele, então precisa sair
 * antes de os overrides serem reaplicados, senão um campo apagado voltaria.
 *
 * `mobile_phone` fica de fora de propósito: ele vem do Graph, e o snapshot é o
 * único caminho desse valor até o runtime de background. Limpá-lo aqui zeraria
 * o celular de quem tem o atributo preenchido no Microsoft 365 e nunca editou.
 */
const AUTHORED_ONLY_FIELDS = ["certifications", "ramal"] as const;

/**
 * Campos **derivados** (Microsoft Graph e `GENERAL_DATA`) que o usuário pode
 * sobrescrever quando o administrador libera a edição (`allow_editing`).
 *
 * Diferente de {@link EDITABLE_FIELDS}, estes têm um valor de origem que precisa
 * sobreviver quando não há override  limpar um campo desses significa "voltar
 * ao padrão", não "deixar vazio".
 */
export const OVERRIDABLE_FIELDS = [
  "display_name",
  "email",
  "job_title",
  "office_phone_1",
  "office_phone_2",
  "street_address",
  "city",
  "state",
  "zip_code",
  "country",
] as const;

/**
 * Campos cujo valor de origem é uma constante em `GENERAL_DATA`, e não o Graph.
 *
 * Endereço ainda não entra aqui: `normalizeUserData` continua forçando
 * `GENERAL_DATA` para esses campos.
 */
const INSTITUTIONAL_FALLBACK_FIELDS = ["office_phone_1", "office_phone_2"] as const;

/** Todo campo que aceita um valor digitado pelo usuário. */
export const ALL_OVERRIDE_FIELDS = [...EDITABLE_FIELDS, ...OVERRIDABLE_FIELDS] as const;

export type EditableField = (typeof EDITABLE_FIELDS)[number];
export type OverridableField = (typeof OVERRIDABLE_FIELDS)[number];
export type OverrideField = (typeof ALL_OVERRIDE_FIELDS)[number];

/** Valores digitados pelo usuário, indexados por {@link ALL_OVERRIDE_FIELDS}. */
export type UserOverrides = Partial<Record<OverrideField, string>>;

/**
 * Campos que `normalizeUserData` deriva de um override e que, por isso, precisam
 * sair do snapshot quando esse override existe  senão o valor antigo venceria
 * na derivação (ex.: `first_name` continuaria vindo do `name` anterior).
 */
const OVERRIDE_ALIASES: Partial<Record<OverrideField, readonly string[]>> = {
  display_name: ["name", "first_name", "last_name"],
  job_title: ["job"],
};

function readOverrides(): UserOverrides {
  try {
    const raw = localStorage.getItem(OVERRIDES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: UserOverrides = {};
    for (const field of ALL_OVERRIDE_FIELDS) {
      const value = parsed[field];
      if (typeof value === "string" && value !== "") out[field] = value;
    }
    return out;
  } catch {
    warn(SOURCE, "readOverrides: erro ao parsear overrides.");
    return {};
  }
}

/**
 * Remove do snapshot os campos que os overrides devem resolver.
 *
 * O snapshot persistido guarda os valores já mesclados (o runtime de background
 * lê o roaming direto, sem acesso aos overrides), então sem essa limpeza um
 * valor apagado pelo usuário voltaria do próprio snapshot na gravação seguinte.
 *
 * Os {@link AUTHORED_ONLY_FIELDS} e os {@link INSTITUTIONAL_FALLBACK_FIELDS}
 * saem sempre  os primeiros não têm outra fonte, os segundos são recompostos
 * por `normalizeUserData` a partir de `GENERAL_DATA`. Os campos derivados do
 * Graph ficam, porque o valor herdado é justamente o fallback de quem não
 * sobrescreveu.
 */
function withoutOverrideFields(
  data: Record<string, any>,
  overrides: UserOverrides
): Record<string, any> {
  const base = { ...data };
  for (const field of AUTHORED_ONLY_FIELDS) delete base[field];
  for (const field of INSTITUTIONAL_FALLBACK_FIELDS) delete base[field];

  for (const [field, aliases] of Object.entries(OVERRIDE_ALIASES)) {
    if (!overrides[field as OverrideField]) continue;
    for (const alias of aliases as readonly string[]) delete base[alias];
  }

  return base;
}

/** Overrides que precisam passar por `formatPhoneNumber` ao serem reaplicados. */
const PHONE_FIELDS = new Set<string>(["office_phone_1", "office_phone_2", "mobile_phone"]);

/**
 * Reaplica os overrides por cima do resultado de `normalizeUserData`.
 *
 * Necessário porque `normalizeUserData` força `GENERAL_DATA` para o endereço
 * independente do que receber  um override desses campos só sobrevive se for
 * reaplicado depois, não antes, da normalização. Os telefones fixos já são
 * respeitados na normalização, mas continuam aqui para receber a formatação.
 */
function applyOverrides(
  normalized: UserTemplateData,
  overrides: UserOverrides
): UserTemplateData {
  const result: Record<string, any> = { ...normalized };
  for (const [field, value] of Object.entries(overrides)) {
    result[field] = PHONE_FIELDS.has(field) ? formatPhoneNumber(value) : value;
  }
  return result as UserTemplateData;
}

function readSnapshot(): Record<string, any> | null {
  const raw = localStorage.getItem(LOCAL_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      debug(SOURCE, `readSnapshot: localStorage campos=${Object.keys(parsed).length}`);
      return parsed;
    } catch {
      warn(SOURCE, "readSnapshot: erro ao parsear localStorage.");
    }
  }

  // Runtimes separados do add-in (background do launch event) não compartilham
  // localStorage com o taskpane  roamingSettings é a única ponte entre eles.
  if (typeof Office !== "undefined" && Office.context?.roamingSettings) {
    const roamingRaw = Office.context.roamingSettings.get(ROAMING_KEY);
    if (roamingRaw) {
      try {
        const parsed = typeof roamingRaw === "string" ? JSON.parse(roamingRaw) : roamingRaw;
        debug(SOURCE, `readSnapshot: roamingSettings campos=${Object.keys(parsed).length}`);
        return parsed;
      } catch {
        warn(SOURCE, "readSnapshot: erro ao parsear roamingSettings.");
      }
    }
  }

  return null;
}

/**
 * Cache dos dados que compõem as assinaturas do usuário.
 *
 * Guarda duas coisas distintas:
 * - um **snapshot** derivado (Graph + `GENERAL_DATA`), descartável e sempre
 *   recomposto no próximo login existe só para o runtime de background
 *   conseguir renderizar a assinatura sem acesso ao Graph;
 * - os **overrides** do usuário ({@link ALL_OVERRIDE_FIELDS}), que sempre vencem
 *   o snapshot. Os {@link EDITABLE_FIELDS} não têm outra fonte de onde voltar; os
 *   {@link OVERRIDABLE_FIELDS} têm, e limpar um deles devolve o valor derivado.
 *
 * @example
 * ```typescript
 * import { userDataCache } from "@/shared/services/user-data-cache";
 *
 * const data = userDataCache.get();
 * userDataCache.saveOverrides({ ramal: "167" });
 * userDataCache.refreshFromGraph({ email: "novo@e.com", display_name: "João" });
 * ```
 */
export const userDataCache = {
  /**
   * Recupera os dados do usuário normalizados, com os overrides aplicados por cima.
   *
   * @returns Dados normalizados do usuário ou `null` se nenhum snapshot existir.
   *
   * @example
   * userDataCache.get();
   */
  get(): UserTemplateData | null {
    const snapshot = readSnapshot();
    if (!snapshot) {
      debug(SOURCE, "get: nenhum cache encontrado.");
      return null;
    }
    const overrides = readOverrides();
    const normalized = normalizeUserData({ ...withoutOverrideFields(snapshot, overrides), ...overrides });
    return applyOverrides(normalized, overrides);
  },

  /**
   * Recupera apenas os valores digitados pelo usuário.
   *
   * @returns Overrides persistidos (pode ser objeto vazio).
   *
   * @example
   * userDataCache.getOverrides();
   */
  getOverrides(): UserOverrides {
    return readOverrides();
  },

  /**
   * Registra o e-mail real da conta (o `mail` do Microsoft Graph) como identidade.
   *
   * Não usa `saveAsync`: quem dispara o fluxo é que persiste o roaming, para
   * evitar chamadas concorrentes.
   *
   * @param email - E-mail vindo do Graph.
   *
   * @example
   * userDataCache.setIdentityEmail(profile.mail);
   */
  setIdentityEmail(email: string): void {
    const value = (email || "").trim();
    if (!value) return;

    try {
      localStorage.setItem(IDENTITY_LOCAL_KEY, value);
    } catch {
      warn(SOURCE, "setIdentityEmail: erro ao salvar no localStorage.");
    }

    if (typeof Office !== "undefined" && Office.context?.roamingSettings) {
      try {
        Office.context.roamingSettings.set(IDENTITY_ROAMING_KEY, value);
      } catch {
        warn(SOURCE, "setIdentityEmail: erro ao salvar no roamingSettings.");
      }
    }
  },

  /**
   * E-mail que identifica o usuário perante a plataforma.
   *
   * É **sempre** este valor  nunca o `email` exibido  que vai no cabeçalho
   * `X-User-Email`. O `email` do snapshot é sobrescrevível pelo usuário quando o
   * administrador libera a edição, e usá-lo na API faria o backend
   * auto-cadastrar um recipient novo (com `allow_editing` falso), trancando o
   * usuário fora da própria permissão.
   *
   * @returns E-mail de identidade, ou string vazia se ainda não houver.
   *
   * @example
   * await fetchSignatureTemplates(userDataCache.identityEmail());
   */
  identityEmail(): string {
    const stored = getFromRoamingOrLocal(IDENTITY_ROAMING_KEY, IDENTITY_LOCAL_KEY);
    if (stored) return stored;

    // Instalações anteriores à identidade separada: o snapshot ainda é a única
    // fonte, e nesse momento o `email` não podia ter sido editado.
    return readSnapshot()?.email || "";
  },

  /**
   * Persiste os overrides digitados pelo usuário e reescreve o snapshot para o
   * runtime de background enxergar a mudança imediatamente.
   *
   * Os {@link EDITABLE_FIELDS} são sempre gravados. Os {@link OVERRIDABLE_FIELDS}
   * só são gravados quando a edição está liberada; com a edição bloqueada, os
   * overrides já existentes desses campos são preservados, nunca alterados.
   * Campo vazio remove o override e devolve o valor derivado.
   *
   * @param overrides - Valores dos campos em {@link ALL_OVERRIDE_FIELDS}.
   *
   * @example
   * userDataCache.saveOverrides({ ramal: "167", city: "Campinas" });
   */
  saveOverrides(overrides: UserOverrides): void {
    const allowEdit = profilePermissions.allowEditing();
    const existing = readOverrides();
    const next: UserOverrides = {};

    for (const field of EDITABLE_FIELDS) {
      const value = (overrides[field] ?? "").trim();
      if (value) next[field] = value;
    }

    for (const field of OVERRIDABLE_FIELDS) {
      if (allowEdit) {
        const value = (overrides[field] ?? "").trim();
        if (value) next[field] = value;
      } else if (existing[field]) {
        next[field] = existing[field];
      }
    }

    try {
      localStorage.setItem(OVERRIDES_KEY, JSON.stringify(next));
    } catch {
      warn(SOURCE, "saveOverrides: erro ao salvar no localStorage.");
    }
    info(SOURCE, `saveOverrides: ${Object.keys(next).length} campo(s) preenchido(s).`);

    this.save(readSnapshot() ?? {});
  },

  /**
   * Salva o snapshot derivado, com os overrides do usuário aplicados por cima.
   *
   * @param data - Dados derivados (Graph + institucionais) a persistir.
   *
   * @example
   * userDataCache.save({ name: "João", email: "j@e.com" });
   */
  save(data: Partial<UserTemplateData>): void {
    const overrides = readOverrides();
    const normalized = applyOverrides(
      normalizeUserData({ ...withoutOverrideFields(data, overrides), ...overrides }),
      overrides
    );
    info(SOURCE, `save: ${Object.values(normalized).filter(Boolean).length} campos preenchidos.`);

    try {
      localStorage.setItem(LOCAL_KEY, JSON.stringify(normalized));
    } catch {
      warn(SOURCE, "save: erro ao salvar no localStorage.");
    }

    if (normalized.email || normalized.name) setupState.markComplete();

    if (typeof Office !== "undefined" && Office.context?.roamingSettings) {
      try {
        // A foto em base64 estoura o limite de tamanho do roamingSettings.
        const { photo, photo_base64, ...rest } = normalized as Record<string, any>;
        Office.context.roamingSettings.set(ROAMING_KEY, JSON.stringify(rest));
        // saveAsync é chamado pelo fluxo que disparou o save para evitar
        // chamadas concorrentes ao roamingSettings.
      } catch {
        warn(SOURCE, "save: erro ao salvar no roamingSettings.");
      }
    }
  },

  /**
   * Substitui o snapshot pelos dados frescos do Graph. Os overrides do usuário
   * são reaplicados pelo {@link userDataCache.save} e nunca são sobrescritos.
   *
   * @param graphData - Dados vindos do Microsoft Graph.
   *
   * @example
   * userDataCache.refreshFromGraph(mapGraphProfileToUserData(profile));
   */
  refreshFromGraph(graphData: Partial<UserTemplateData>): void {
    const merged: Record<string, any> = { ...(readSnapshot() ?? {}) };

    for (const [key, value] of Object.entries(graphData)) {
      if (value !== undefined && value !== null && value !== "") merged[key] = value;
    }

    info(SOURCE, `refreshFromGraph: ${Object.keys(graphData).length} campos recebidos do Graph.`);
    this.save(merged);
  },

  /**
   * Remove snapshot e overrides, em todos os armazenamentos.
   *
   * **Não** limpa a flag de configuração ({@link setupState}): "Restaurar
   * padrões" chama este método para recompor os dados a partir do Graph, e
   * apagar a flag ali mandaria o usuário de volta ao onboarding. Só o logout
   * limpa a flag.
   *
   * @example
   * userDataCache.clear();
   */
  clear(): void {
    localStorage.removeItem(LOCAL_KEY);
    localStorage.removeItem(OVERRIDES_KEY);
    localStorage.removeItem(LEGACY_META_KEY);
    localStorage.removeItem(IDENTITY_LOCAL_KEY);
    if (typeof Office !== "undefined" && Office.context?.roamingSettings) {
      Office.context.roamingSettings.remove(ROAMING_KEY);
      Office.context.roamingSettings.remove(IDENTITY_ROAMING_KEY);
      void flushRoaming();
    }
    info(SOURCE, "clear: cache e overrides removidos.");
  },
};
